import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  ORDEN_SECCIONES,
  decisionesDelServidor,
  indiceDeNumero,
  moverCursor,
  primeraPendiente,
  siguientePorMarcar,
  tarjetasDeSeccion,
  type ColaRead,
  type DecisionLocal,
  type SeccionClave,
  type TarjetaCola,
} from '@/lib/bateria-cola'

/** La cola es una lista congelada y el cursor una posición sobre ella: `←`, `→` y decidir mueven ±1. */
export type CursorDeLaCola = {
  seccion: SeccionClave | null
  indice: number
  /** `'lote'` es provisional: puede declararse sobre un prefijo de tramos. Ver `mostrarCierreLote`. */
  fase: 'tarjetas' | 'cierre' | 'lote'
  /** Las de la sección, o las de la ronda de repaso si hay una. */
  deLaSeccion: TarjetaCola[]
  /** Incluye los tramos por llegar: es el «de M» y el techo de «Ir a #». En una ronda, su largo. */
  totalDeLaSeccion: number
  /** La tarjeta bajo el cursor, o `undefined` si su tramo todavía no llegó. */
  actual: TarjetaCola | undefined
  siguienteSeccion: SeccionClave | null
  enRonda: boolean
  /** Después de DECIDIR: UNA posición hacia adelante, o al cierre de la sección. */
  avanzar: () => void
  atras: () => void
  adelante: () => void
  entrarASeccion: (clave: SeccionClave) => void
  entrarARonda: () => void
  irATarjeta: (tarjeta: TarjetaCola) => void
  /** `false` si no queda ninguna por delante. */
  irASiguientePorMarcar: () => boolean
  /** «Ir a #»: el número que se ve en la tarjeta. `false` si no era un número dentro del rango. */
  saltarA: (numero: string) => boolean
  continuar: () => void
  reiniciar: () => void
}

/** `fase==='lote'` no basta: mientras la cadena de tramos cargue o esté en error, «terminada» sería falso. */
export function mostrarCierreLote(
  fase: CursorDeLaCola['fase'],
  cargandoTramos: boolean,
  errorTramos: boolean
): boolean {
  return fase === 'lote' && !cargandoTramos && !errorTramos
}

export function useCursorCola({
  cola,
  tarjetas,
  totales,
  decisiones,
  dejadas,
  porMarcar,
  seccionInicial,
  alMover,
}: {
  cola: ColaRead | undefined
  tarjetas: TarjetaCola[]
  /** El tamaño de cada sección según el SERVIDOR, que incluye los tramos por llegar. */
  totales: Partial<Record<SeccionClave, number>>
  decisiones: Record<string, DecisionLocal>
  dejadas: Record<string, true>
  /** Lo pone la pantalla: es `duda_pendiente` menos lo que esta sesión resolvió. */
  porMarcar: (tarjeta: TarjetaCola) => boolean
  /** `?ir_a=`: navegación, no filtro; solo cambia dónde arranca el cursor. */
  seccionInicial?: SeccionClave | null
  /** Suelta el gesto en curso y el destino de una hoja al cambiar de tarjeta. */
  alMover: () => void
}): CursorDeLaCola {
  const [seccion, setSeccion] = useState<SeccionClave | null>(null)
  const [indice, setIndice] = useState(0)
  const [fase, setFase] = useState<'tarjetas' | 'cierre' | 'lote'>('tarjetas')
  // Congelada al entrar: recalculada, la ronda se encogería bajo la mano («3 de 9» pasaría a «3 de 8»).
  const [ronda, setRonda] = useState<string[] | null>(null)

  const deLaSeccion = useMemo(() => {
    const suyas = seccion ? tarjetasDeSeccion(tarjetas, seccion) : []
    return ronda ? suyas.filter((tarjeta) => ronda.includes(tarjeta.ref)) : suyas
  }, [tarjetas, seccion, ronda])
  const actual: TarjetaCola | undefined = deLaSeccion[indice]
  const totalDeLaSeccion = ronda || !seccion ? deLaSeccion.length : (totales[seccion] ?? deLaSeccion.length)

  // Arranca una vez en la primera pendiente (lo ya decidido no cuenta) o, con `?ir_a=`, en esa sección
  // aunque su tramo no haya llegado. `setFase` va en las dos ramas: si no, la fase quedaba en `lote`.
  useEffect(() => {
    if (!cola || seccion !== null) return
    const pedida = seccionInicial ?? null
    if (pedida !== null && (totales[pedida] ?? tarjetasDeSeccion(cola.tarjetas, pedida).length) > 0) {
      setSeccion(pedida)
      setIndice(0)
      setFase('tarjetas')
      return
    }
    const sembradas = decisionesDelServidor(cola.tarjetas)
    const destino = primeraPendiente(cola.tarjetas, (ref) => ref in sembradas)
    if (destino === null) {
      setFase('lote')
      return
    }
    setSeccion(destino.seccion)
    setIndice(destino.indice)
    setFase('tarjetas')
    // `totales` y `seccionInicial` vienen de la misma consulta o de la URL (que remonta): su guarda ya corta el efecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cola, seccion])

  const cerrarSeccion = useCallback(() => {
    setRonda(null)
    setFase('cierre')
  }, [])

  /** Una posición, decidida o no: el salto al trabajo que falta es `irASiguientePorMarcar`. */
  const avanzar = useCallback(() => {
    alMover()
    if (indice + 1 < deLaSeccion.length) {
      setIndice(indice + 1)
      return
    }
    // Con tramos en camino la sección no se cierra: se salta a la primera posición que no ha llegado.
    const enCamino = Math.max(indice + 1, deLaSeccion.length)
    if (!ronda && enCamino < totalDeLaSeccion) setIndice(enCamino)
    else cerrarSeccion()
  }, [deLaSeccion, indice, totalDeLaSeccion, ronda, alMover, cerrarSeccion])

  /** Cruza el borde de la sección; en una ronda, salirse por cualquier punta devuelve al cierre. */
  const mover = useCallback(
    (delta: 1 | -1) => {
      if (ronda && fase === 'tarjetas') {
        alMover()
        const destino = indice + delta
        if (destino < 0 || destino >= deLaSeccion.length) cerrarSeccion()
        else setIndice(destino)
        return
      }
      // Un cursor más allá de lo cargado no existe en la lista de nodos: se mueve por posición en la sección.
      if (fase === 'tarjetas' && indice >= deLaSeccion.length) {
        alMover()
        setIndice(Math.max(0, Math.min(indice + delta, totalDeLaSeccion - 1)))
        return
      }
      const destino = moverCursor(tarjetas, {fase, seccion, indice}, delta)
      if (!destino) return
      alMover()
      setSeccion(destino.seccion)
      setIndice(destino.indice)
      setFase(destino.fase)
    },
    [tarjetas, fase, seccion, indice, ronda, deLaSeccion, totalDeLaSeccion, alMover, cerrarSeccion]
  )

  const atras = useCallback(() => mover(-1), [mover])
  const adelante = useCallback(() => mover(1), [mover])

  const entrarASeccion = useCallback(
    (clave: SeccionClave) => {
      const suyas = tarjetasDeSeccion(tarjetas, clave)
      const primera = suyas.findIndex((tarjeta) => !(tarjeta.ref in decisiones))
      // Una sección con tramos por llegar no está terminada: entra a la primera posición que falta.
      const faltanTramos = suyas.length < (totales[clave] ?? 0)
      alMover()
      setRonda(null)
      setSeccion(clave)
      setIndice(primera !== -1 ? primera : faltanTramos ? suyas.length : 0)
      setFase(primera === -1 && !faltanTramos ? 'cierre' : 'tarjetas')
    },
    [tarjetas, totales, decisiones, alMover]
  )

  /** Se rearma en cada entrada desde las dejadas del momento: el botón nunca queda muerto. */
  const entrarARonda = useCallback(() => {
    if (!seccion) return
    const refs = tarjetasDeSeccion(tarjetas, seccion)
      .filter((tarjeta) => tarjeta.ref in dejadas)
      .map((tarjeta) => tarjeta.ref)
    if (refs.length === 0) return
    alMover()
    setRonda(refs)
    setIndice(0)
    setFase('tarjetas')
  }, [tarjetas, seccion, dejadas, alMover])

  const irATarjeta = useCallback(
    (tarjeta: TarjetaCola) => {
      const suyas = tarjetasDeSeccion(tarjetas, tarjeta.seccion)
      alMover()
      setRonda(null)
      setSeccion(tarjeta.seccion)
      setIndice(Math.max(0, suyas.indexOf(tarjeta)))
      setFase('tarjetas')
    },
    [tarjetas, alMover]
  )

  /** Sale de la ronda de repaso: el trabajo que falta es del lote. */
  const irASiguientePorMarcar = useCallback(() => {
    const destino = siguientePorMarcar(tarjetas, {seccion, indice: ronda ? -1 : indice}, porMarcar)
    if (destino === null) return false
    alMover()
    setRonda(null)
    setSeccion(destino.seccion)
    setIndice(destino.indice)
    setFase('tarjetas')
    return true
  }, [tarjetas, seccion, indice, ronda, porMarcar, alMover])

  // Contra la sección entera, no contra lo cargado: el número es el que la tarjeta muestra.
  const saltarA = useCallback(
    (numero: string) => {
      const destino = indiceDeNumero(totalDeLaSeccion, numero)
      if (destino === null) return false
      alMover()
      setIndice(destino)
      setFase('tarjetas')
      return true
    },
    [totalDeLaSeccion, alMover]
  )

  const siguienteSeccion = useMemo(() => {
    if (!seccion) return null
    const resto = ORDEN_SECCIONES.slice(ORDEN_SECCIONES.indexOf(seccion) + 1)
    // Vacía según el servidor, no lo cargado: si no, se saltaría una sección cuyo tramo viene en camino.
    return resto.find((clave) => (totales[clave] ?? tarjetasDeSeccion(tarjetas, clave).length) > 0) ?? null
  }, [seccion, tarjetas, totales])

  const continuar = useCallback(() => {
    setRonda(null)
    if (siguienteSeccion) entrarASeccion(siguienteSeccion)
    else setFase('lote')
  }, [siguienteSeccion, entrarASeccion])

  const reiniciar = useCallback(() => {
    setRonda(null)
    setSeccion(null)
    setIndice(0)
    setFase('tarjetas')
  }, [])

  return {
    seccion,
    indice,
    fase,
    deLaSeccion,
    totalDeLaSeccion,
    actual,
    siguienteSeccion,
    enRonda: ronda !== null,
    avanzar,
    atras,
    adelante,
    entrarASeccion,
    entrarARonda,
    irATarjeta,
    irASiguientePorMarcar,
    saltarA,
    continuar,
    reiniciar,
  }
}
